import React, { useState } from 'react';
import ExpandSection from 'src/components/ExpandSection';
import { QKeys } from 'src/api/types';
import Button from 'src/components/form/Button';
import { LinkIncidentsToScenarioModal } from 'src/components/modals';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useVersion from 'src/hooks/useVersion';
import { NoData, Spacer } from 'src/common';
import GenericTable, { TableSheetIncident } from 'src/components/GenericTable';
import { TableIds } from 'src/api/types/misc';
import { queryCache } from 'react-query';
import { useColumnsIncidents } from './useColumnsIncidents';
import { ScenarioQuery, useUnlinkIncidentFromScenariosMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql';

interface IncidentsSectionProps {
  scenario: NonNullable<ScenarioQuery['scenario']>;
}

export const IncidentsSection: React.FC<IncidentsSectionProps> = ({ scenario }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { version } = useVersion();
  const { refetchQuery } = useCache();

  const [unlink] = useUnlinkIncidentFromScenariosMutation({
    onCompleted: () => {
      queryCache.invalidateQueries([QKeys.Scenario, { scenarioId: scenario.id, variant: 'base' }]);
      queryCache.invalidateQueries([QKeys.Scenario, { scenarioId: scenario.id }]);
      refetchQuery({ include: ['scenario'] });
    },
  });

  const { columns, GTColumnSelectAnchorExported } = useColumnsIncidents({
    data: scenario.incidents,
    tdm: {
      onUnlinkIncidentClick: (incidentId) =>
        unlink({
          variables: {
            incidentId,
            scenarioIds: [scenario.id],
          },
        }),
    },
  });

  return (
    <>
      {isModalOpen && (
        <LinkIncidentsToScenarioModal
          onClose={() => setIsModalOpen(false)}
          scenarioId={scenario.id}
          subTitle={{
            title: 'SCENARIO',
            value: scenario.name || '',
          }}
        />
      )}
      <ExpandSection
        title="INCIDENTS"
        sectionId="scenario_incidents"
        onChange={setIsOpen}
        endComponent={
          isOpen && !version ? (
            <Button
              primary
              onClick={() => {
                setIsModalOpen(true);
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Add incidents',
                  tags: ['SCENARIO', 'INCIDENT'],
                });
              }}
              data-cy="add-incidents-btn"
              $constWidth={200}
            >
              + ADD INCIDENTS
            </Button>
          ) : undefined
        }
      >
        {scenario.incidents.length ? (
          <>
            <Spacer $px={20} />
            <GenericTable
              tableId={TableIds.scenarioIncidents}
              data={scenario.incidents}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              expandContent={(data) => (
                <TableSheetIncident
                  data={{
                    id: data.id,
                    name: data.name,
                    source: data.source,
                    dateOfIncident: data.dateOfIncident,
                    description: data.description,
                    ref: data.ref,
                    scenarios: [],
                    financialImpactLower: data.financialImpactLower,
                    financialImpactUpper: data.financialImpactUpper,
                    impact: data.impact,
                    incidentType: data.incidentType,
                    owner: data.owner,
                    severity: data.severity,
                  }}
                />
              )}
              searchable={['ref', 'name', 'incidentType', 'dateOfIncident']}
            />
          </>
        ) : (
          <>
            <Spacer $px={5} />
            <NoData title="No incidents linked" />
          </>
        )}
      </ExpandSection>
    </>
  );
};
